/**
 * A tela de Tarefas (`/tarefas`) — a fila de trabalho inteira (4.6; reescrita na 11.6, FR-18,
 * FR-20, FR-21, FR-22, FR-80).
 *
 * **Recorte é do servidor, e não um `filter` sobre a lista em mãos.** As quatro abas de prazo e os
 * seis filtros da 11.6 (criador, Responsável, Tipo, Negócio, Empresa, Pessoa) entram na chave de
 * cache e na URL do `GET /tarefas`: recortar aqui daria a resposta certa hoje e a errada no dia em
 * que a lista for paginada. Os filtros se somam (interseção), e quem decide o que é **visível** é
 * a policy (AD-4) — o filtro é recorte, nunca autorização.
 *
 * **As quatro abas mandam `concluida=false`**: `atrasadas` já implica pendente, mas `hoje` e
 * `futuras` falam só de prazo — sem o segundo parâmetro a aba "Hoje" traria o que já foi feito.
 *
 * **Os seis filtros são chips na barra, e persistem por tela na sessão** (`sessionStorage`, só
 * ids — nada de credencial, AD-5). O Responsável é multi, com "Eu" fixo no topo; os demais são um
 * por vez. As opções de Negócio, Empresa e Pessoa saem das Tarefas que a aba já trouxe **sem** os
 * chips: é o universo em que filtrar faz sentido, e não custa ida à rede quando nenhum chip está
 * ligado (a chave é a mesma).
 *
 * **A tabela do computador é TanStack Table** (v9, molde de `AguardandoEntrega`), com o Tipo como
 * ícone + nome — o ícone vem do catálogo (11.6), e nunca sozinho: sem ícone, só o nome. O rótulo
 * é o **congelado** no registro (FR-69), e por isso uma Tarefa de Tipo excluído continua se
 * identificando.
 *
 * **O prazo é instante** (11.6): `prazoNaLista` escreve "hoje, 14:00" quando há hora e só o dia
 * quando a Tarefa vale o dia inteiro (23:59:59 em São Paulo). O vermelho vem de `vencida`, que o
 * serviço deriva — a tela não compara relógio nenhum.
 *
 * **Criar avulsa, editar e excluir moram aqui** (11.6, FR-80). "Nova tarefa" abre o
 * `DialogoDaTarefa` sem vínculo de Negócio; "Editar" abre o mesmo diálogo com a Tarefa; "Excluir"
 * pede confirmação. O botão aparece para quem vê a linha — o serviço responde `403` a quem não é
 * Responsável, criador ou gestão, e a mensagem chega verbatim.
 *
 * **"Agendar sugestão" abre um `Dialogo` de criação pré-preenchido**, com o Tipo e o prazo que o
 * **serviço** sugeriu (configurado por Resultado, ou mesmo Tipo em 3 dias). Nada é criado sem o
 * submit humano (FR-22).
 *
 * **A tela vira árvore de cards no celular por `useEhCelular()`**, e nunca por `@media`: a tabela
 * e a `<ul>` são árvores diferentes, e uma media query deixaria a tabela no DOM do leitor de tela.
 *
 * **O filtro de prazo ativo é publicado como subtítulo do shell** (`useDefinirSubtitulo`).
 */

import {
  type ColumnDef,
  createColumnHelper,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useNomesDeEmpresas, useNomesDePessoas } from '@/modulos/cadastro/api'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { AcoesDoCabecalho, useDefinirSubtitulo, useEhCelular } from '@/shell/Shell'
import { ehDeGestao } from '@/shell/navegacao'
import { ConclusaoDeTarefa } from './ConclusaoDeTarefa'
import { LinkDoNegocio } from './navegacao-do-negocio'
import { Dialogo } from './Dialogo'
import { DialogoDaTarefa } from './DialogoDaTarefa'
import { IconeDoTipo, nomeComIcone } from './IconeDoTipo'
import { NovaTarefaDaSugestao } from './NovaTarefaDaSugestao'
import {
  COLUNAS_DAS_TAREFAS,
  ESCOPO_DAS_TAREFAS,
  ESCOPO_DAS_TAREFAS_DA_GESTAO,
  EU,
  FILTROS_DAS_TAREFAS,
  type FiltroDasTarefas,
  LIMPAR_FILTROS,
  PRAZO_VENCIDO,
  QUALQUER,
  ROTULOS_DOS_FILTROS_DAS_TAREFAS,
  ROTULO_DA_TELA_DE_TAREFAS,
  SEM_TAREFAS_COM_FILTROS,
  SEM_TAREFAS_NO_RECORTE,
  prazoNaLista,
  TAREFA_SEM_EMPRESA,
} from './apresentacao'
import { anunciar } from './avisos'
import {
  type OrigemDoTituloDaTarefa,
  rotuloDoResponsavel,
  tituloDaTarefa,
} from './regras-de-tarefa'
import {
  type SugestaoDeProximoPasso,
  type Tarefa,
  useExcluirTarefa,
  useTarefas,
  useTiposDeTarefa,
} from './api'
import './tarefas.css'

/** Enquanto a lista viaja. Sem o aviso, "sem tarefas" e "ainda não chegou" ficam iguais. */
const CARREGANDO = 'Carregando as tarefas…'

/** A saída da recusa: a tela não some, e há o que fazer. */
const TENTAR_DE_NOVO = 'Tentar de novo'

/** O rótulo do grupo de abas, para quem navega por leitor de tela. */
const ROTULO_DOS_FILTROS = 'Filtrar as tarefas por prazo'

/**
 * Os `id` dos controles da barra — fixos, e não derivados do rótulo: um rótulo com acento
 * ("Negócio") viraria `id` com acento, e renomear o texto quebraria o `htmlFor` e os testes.
 */
const IDS_DOS_FILTROS = {
  criador: 'filtro-das-tarefas-criador',
  tipo: 'filtro-das-tarefas-tipo',
  negocio: 'filtro-das-tarefas-negocio',
  empresa: 'filtro-das-tarefas-empresa',
  pessoa: 'filtro-das-tarefas-pessoa',
} as const

/** O rótulo da barra de chips (11.6). */
const ROTULO_DA_BARRA_DE_FILTROS = 'Filtros das tarefas'

/** O aviso de que os nomes das Empresas não vieram — e que a linha **não** é "sem empresa". */
const NOMES_NAO_VIERAM =
  'Não foi possível carregar os nomes das empresas; as linhas mostram o título sozinho.'

/** O que o leitor de tela ouve enquanto o nome da Empresa ainda está no ar. */
const NOME_A_CAMINHO = ' (carregando o nome da empresa)'

/** O aviso de que a equipe não veio: sem ele a linha fica em "responsável a caminho…" para sempre. */
const EQUIPE_NAO_VEIO =
  'Não foi possível carregar os nomes da equipe; as tarefas de outras pessoas ficam sem o nome ' +
  'de quem responde por elas.'

/** Os rótulos das ações da linha. "Concluir" é literal do protótipo (`:375`). */
const CONCLUIR = 'Concluir'
export const EDITAR = 'Editar'
export const EXCLUIR = 'Excluir'
export const NOVA_TAREFA = 'Nova tarefa'
/** O texto visível do botão do cabeçalho (protótipo v2); o nome acessível continua `NOVA_TAREFA`. */
const MAIS_TAREFA = '+ Tarefa'
export const ROTULO_DOS_FILTROS_DA_TELA = 'Filtros'
export const ROTULO_DE_OCULTAR_OS_FILTROS = 'Ocultar filtros'
const MAIS_ACOES = 'Mais ações'
export const NOTA_DO_RODAPE =
  'Editar e excluir valem também para tarefa concluída — e ficam na trilha de auditoria. ' +
  'Quem pode: o responsável, quem criou, Admin e Gestor.'
function contagemDeTarefas(total: number): string {
  return `${String(total)} tarefa${total === 1 ? '' : 's'}`
}

/** O escolhido que a aba atual não traz (persistido na sessão, de outra aba). */
const FILTRO_FORA_DA_ABA = 'Escolhido (fora desta aba)'

export const TITULO_DA_EXCLUSAO = 'Excluir tarefa'
export const SUBTITULO_DA_EXCLUSAO =
  'A tarefa e os anexos dela deixam de existir. A trilha de auditoria registra a exclusão.'
export const TAREFA_EXCLUIDA = 'Tarefa excluída.'

/** A chave da persistência dos chips — por tela, na sessão do navegador. */
export const CHAVE_DOS_FILTROS_NA_SESSAO = 'crm.tarefas.filtros'

/** Os seis filtros da barra (11.6). Vazio é "sem filtro". */
export interface FiltrosDaTela {
  criadorId: string
  responsavelIds: string[]
  tipoDeTarefaId: string
  negocioId: string
  empresaId: string
  pessoaId: string
}

export const SEM_FILTROS: FiltrosDaTela = {
  criadorId: '',
  responsavelIds: [],
  tipoDeTarefaId: '',
  negocioId: '',
  empresaId: '',
  pessoaId: '',
}

/** Lê os chips da sessão; qualquer coisa fora da forma volta ao vazio (e nunca derruba a tela). */
function lerFiltrosDaSessao(): FiltrosDaTela {
  try {
    const cru = window.sessionStorage.getItem(CHAVE_DOS_FILTROS_NA_SESSAO)
    if (cru === null) {
      return SEM_FILTROS
    }
    const lido = JSON.parse(cru) as Partial<Record<keyof FiltrosDaTela, unknown>>
    const texto = (valor: unknown) => (typeof valor === 'string' ? valor : '')
    return {
      criadorId: texto(lido.criadorId),
      responsavelIds: Array.isArray(lido.responsavelIds)
        ? lido.responsavelIds.filter((cada): cada is string => typeof cada === 'string')
        : [],
      tipoDeTarefaId: texto(lido.tipoDeTarefaId),
      negocioId: texto(lido.negocioId),
      empresaId: texto(lido.empresaId),
      pessoaId: texto(lido.pessoaId),
    }
  } catch {
    return SEM_FILTROS
  }
}

function gravarFiltrosNaSessao(filtros: FiltrosDaTela) {
  try {
    window.sessionStorage.setItem(CHAVE_DOS_FILTROS_NA_SESSAO, JSON.stringify(filtros))
  } catch {
    //: Armazenamento recusado (janela privada, cota): o filtro vale só enquanto a tela viver.
  }
}

function temFiltro(filtros: FiltrosDaTela): boolean {
  return (
    filtros.criadorId !== '' ||
    filtros.responsavelIds.length > 0 ||
    filtros.tipoDeTarefaId !== '' ||
    filtros.negocioId !== '' ||
    filtros.empresaId !== '' ||
    filtros.pessoaId !== ''
  )
}

/** `''` vira `undefined`: ausência de filtro é ausência de parâmetro na URL. */
function ouNada(valor: string): string | undefined {
  return valor === '' ? undefined : valor
}

/** A linha da tela, já resolvida — **sem** campo de dinheiro (FR-62). */
interface LinhaDaTarefa {
  tarefa: Tarefa
  titulo: string
  origemDoTitulo: OrigemDoTituloDaTarefa
  prazo: string
  responsavel: string
  /** Quem criou, pelo nome — a segunda linha da célula "Tarefa" (protótipo v2). */
  criador: string
  /** O ícone do Tipo, do catálogo — `null` quando o Tipo não tem (ou saiu do catálogo). */
  icone: string | null
}

interface AcoesDaLinha {
  aoConcluir: (tarefa: Tarefa, evento: MouseEvent<HTMLButtonElement>) => void
  aoEditar: (tarefa: Tarefa, evento: MouseEvent<HTMLButtonElement>) => void
  aoExcluir: (tarefa: Tarefa, evento: MouseEvent<HTMLButtonElement>) => void
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O Tipo: ícone + nome — o ícone nunca é o único sinal (a11y). */
function ChipDoTipo({ linha }: { linha: LinhaDaTarefa }) {
  return (
    <span className="tela-tarefas__tipo">
      {linha.icone !== null ? (
        <span className="tela-tarefas__icone" aria-hidden="true">
          <IconeDoTipo icone={linha.icone} />
        </span>
      ) : null}
      {linha.tarefa.tipo_nome}
    </span>
  )
}

/** O título (a Empresa) e o título da Tarefa, empilhados (protótipo `:369-372`). */
/**
 * A célula "Tarefa" (protótipo v2): o título em negrito e, embaixo, os anexos e quem criou.
 * A Empresa saiu daqui para a célula própria (`VinculoDaLinha`).
 */
function TituloDaLinha({ linha }: { linha: LinhaDaTarefa }) {
  const anexos = linha.tarefa.anexos.length
  return (
    <div className="tela-tarefas__corpo">
      <span
        className="tela-tarefas__titulo-da-tarefa"
        data-concluida={linha.tarefa.concluida_em !== null}
      >
        {linha.tarefa.titulo}
      </span>
      <span className="tela-tarefas__sub">
        {anexos > 0 ? `${String(anexos)} anexo${anexos === 1 ? '' : 's'} · ` : ''}
        {`criada por ${linha.criador}`}
        {linha.tarefa.resultado_nome === null ? '' : ` · resultado: ${linha.tarefa.resultado_nome}`}
      </span>
    </div>
  )
}

/**
 * A célula "Negócio / empresa" (protótipo v2): a Empresa como link que abre o Negócio quando a
 * Tarefa tem um; só o nome quando é de Empresa; o chip "avulsa" quando é solta.
 */
function VinculoDaLinha({ linha }: { linha: LinhaDaTarefa }) {
  if (linha.origemDoTitulo === 'solta') {
    return <span className="tela-tarefas__chip">{TAREFA_SEM_EMPRESA}</span>
  }
  const pendente = linha.origemDoTitulo === 'pendente'
  const nome = (
    <span
      className="tela-tarefas__empresa"
      data-nome={linha.origemDoTitulo}
      aria-busy={pendente || undefined}
    >
      {linha.titulo}
      {pendente ? <span className="tela-tarefas__so-para-leitor">{NOME_A_CAMINHO}</span> : null}
    </span>
  )
  if (linha.tarefa.negocio_id === null) {
    return nome
  }
  return (
    <LinkDoNegocio
      negocioId={linha.tarefa.negocio_id}
      className="tela-tarefas__link-do-negocio"
      aria-label={`Abrir ${linha.titulo}`}
    >
      {nome}
    </LinkDoNegocio>
  )
}

/** O prazo, com o vermelho de `vencida` e o texto ao lado para quem não enxerga cor. */
function PrazoDaLinha({ linha }: { linha: LinhaDaTarefa }) {
  return (
    <span className="tela-tarefas__prazo" data-vencida={linha.tarefa.vencida}>
      {linha.prazo}
      {linha.tarefa.vencida ? (
        <span className="tela-tarefas__so-para-leitor">{PRAZO_VENCIDO}</span>
      ) : null}
    </span>
  )
}

/**
 * As ações da linha. "Concluir" só na pendente (concluir é mão única, `409` na segunda); "Editar"
 * e "Excluir" em toda linha — concluída inclusive (FR-80). O nome acessível carrega o título:
 * vinte botões chamados "Editar" são indistinguíveis por leitor de tela.
 */
function AcoesDaTarefa({ linha, acoes }: { linha: LinhaDaTarefa; acoes: AcoesDaLinha }) {
  const { tarefa } = linha
  return (
    <div className="tela-tarefas__acoes">
      {tarefa.concluida_em === null ? (
        <button
          type="button"
          className="tela-tarefas__acao"
          aria-label={`${CONCLUIR}: ${tarefa.titulo}`}
          onClick={(evento) => {
            acoes.aoConcluir(tarefa, evento)
          }}
        >
          {CONCLUIR}
        </button>
      ) : null}
      {/* Editar e Excluir atrás do "⋯" (protótipo v2, `.menu`): um `<details>` nativo — abre e
          fecha sem estado, fecha ao escolher, e os itens continuam botões com o nome de sempre. */}
      <details className="tela-tarefas__menu">
        <summary className="tela-tarefas__mais" aria-label={`${MAIS_ACOES}: ${tarefa.titulo}`}>
          <span aria-hidden="true">⋯</span>
        </summary>
        <div className="tela-tarefas__menu-lista">
          <button
            type="button"
            className="tela-tarefas__menu-item"
            aria-label={`${EDITAR}: ${tarefa.titulo}`}
            onClick={(evento) => {
              evento.currentTarget.closest('details')?.removeAttribute('open')
              acoes.aoEditar(tarefa, evento)
            }}
          >
            {EDITAR}
          </button>
          <button
            type="button"
            className="tela-tarefas__menu-item tela-tarefas__menu-item--perigo"
            aria-label={`${EXCLUIR}: ${tarefa.titulo}`}
            onClick={(evento) => {
              evento.currentTarget.closest('details')?.removeAttribute('open')
              acoes.aoExcluir(tarefa, evento)
            }}
          >
            {EXCLUIR}
          </button>
        </div>
      </details>
    </div>
  )
}

/** As capacidades da tabela (v9: só o núcleo — a ordem é a do serviço, sem ordenação local). */
const recursos = tableFeatures({})
const coluna = createColumnHelper<typeof recursos, LinhaDaTarefa>()

function montarColunas(acoes: AcoesDaLinha): ColumnDef<typeof recursos, LinhaDaTarefa>[] {
  return coluna.columns([
    coluna.display({
      id: 'tipo',
      header: COLUNAS_DAS_TAREFAS.tipo,
      cell: (contexto) => <ChipDoTipo linha={contexto.row.original} />,
    }),
    coluna.display({
      id: 'titulo',
      header: COLUNAS_DAS_TAREFAS.titulo,
      cell: (contexto) => <TituloDaLinha linha={contexto.row.original} />,
    }),
    coluna.accessor('responsavel', {
      id: 'responsavel',
      header: COLUNAS_DAS_TAREFAS.responsavel,
    }),
    coluna.display({
      id: 'vinculo',
      header: COLUNAS_DAS_TAREFAS.vinculo,
      cell: (contexto) => <VinculoDaLinha linha={contexto.row.original} />,
    }),
    coluna.display({
      id: 'prazo',
      header: COLUNAS_DAS_TAREFAS.prazo,
      cell: (contexto) => <PrazoDaLinha linha={contexto.row.original} />,
    }),
    coluna.display({
      id: 'acao',
      header: COLUNAS_DAS_TAREFAS.acao,
      cell: (contexto) => <AcoesDaTarefa linha={contexto.row.original} acoes={acoes} />,
    }),
  ])
}

/** A tabela do computador — a grade de cinco colunas do protótipo (`:367-375`). */
function TabelaDasTarefas({ linhas, acoes }: { linhas: LinhaDaTarefa[]; acoes: AcoesDaLinha }) {
  const colunas = useMemo(() => montarColunas(acoes), [acoes])
  const tabela = useTable({
    features: recursos,
    columns: colunas,
    data: linhas,
    getRowId: (linha) => linha.tarefa.id,
  })
  return (
    <div className="tela-tarefas__cartao-da-tabela">
    <table className="tela-tarefas__tabela">
      <caption className="tela-tarefas__so-para-leitor">{ROTULO_DA_TELA_DE_TAREFAS}</caption>
      <thead>
        {tabela.getHeaderGroups().map((grupo) => (
          <tr key={grupo.id}>
            {grupo.headers.map((cabecalho) => (
              <th
                key={cabecalho.id}
                scope="col"
                className={
                  cabecalho.column.id === 'titulo'
                    ? 'tela-tarefas__cabecalho'
                    : `tela-tarefas__cabecalho tela-tarefas__cabecalho--${cabecalho.column.id}`
                }
              >
                {/* A coluna das ações não tem título visível (protótipo v2); o leitor o ouve. */}
                {cabecalho.column.id === 'acao' ? (
                  <span className="tela-tarefas__so-para-leitor">
                    <tabela.FlexRender header={cabecalho} />
                  </span>
                ) : (
                  <tabela.FlexRender header={cabecalho} />
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {tabela.getRowModel().rows.map((linha) => (
          <tr key={linha.id} className="tela-tarefas__linha">
            {linha.getAllCells().map((celula) => (
              <td
                key={celula.id}
                className={
                  celula.column.id === 'responsavel' || celula.column.id === 'acao'
                    ? `tela-tarefas__celula tela-tarefas__celula--${celula.column.id}`
                    : 'tela-tarefas__celula'
                }
              >
                <tabela.FlexRender cell={celula} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    <p className="tela-tarefas__rodape-da-tabela">{NOTA_DO_RODAPE}</p>
    </div>
  )
}

/** Um cartão empilhado do celular — a mesma informação (NFR-1), protótipo `:688-698`. */
function CartaoDaTarefa({ linha, acoes }: { linha: LinhaDaTarefa; acoes: AcoesDaLinha }) {
  return (
    <li className="tela-tarefas__cartao" data-vencida={linha.tarefa.vencida}>
      <div className="tela-tarefas__cartao-topo">
        <TituloDaLinha linha={linha} />
        <ChipDoTipo linha={linha} />
      </div>
      <VinculoDaLinha linha={linha} />
      <div className="tela-tarefas__cartao-rodape">
        <PrazoDaLinha linha={linha} />
        <span className="tela-tarefas__responsavel">{linha.responsavel}</span>
      </div>
      <AcoesDaTarefa linha={linha} acoes={acoes} />
    </li>
  )
}

interface OpcaoDoFiltro {
  id: string
  rotulo: string
}

/** Um chip de escolha única — um `<select>` rotulado, com "Todos" como ausência de filtro. */
function ChipDeEscolha({
  id,
  rotulo,
  valor,
  opcoes,
  aoMudar,
}: {
  id: string
  rotulo: string
  valor: string
  opcoes: OpcaoDoFiltro[]
  aoMudar: (valor: string) => void
}) {
  //: O escolhido continua como opção mesmo quando saiu do universo (persistido na sessão).
  const escolhidoForaDaLista = valor !== '' && !opcoes.some((cada) => cada.id === valor)
  return (
    <span className="tela-tarefas__filtro" data-ativo={valor !== ''}>
      <label htmlFor={id}>{rotulo}</label>
      <select
        id={id}
        value={valor}
        onChange={(evento) => {
          aoMudar(evento.target.value)
        }}
      >
        <option value="">{QUALQUER}</option>
        {escolhidoForaDaLista ? <option value={valor}>{FILTRO_FORA_DA_ABA}</option> : null}
        {opcoes.map((cada) => (
          <option key={cada.id} value={cada.id}>
            {cada.rotulo}
          </option>
        ))}
      </select>
    </span>
  )
}

/** O chip do Responsável — multi, com "Eu" fixo no topo. */
function ChipDosResponsaveis({
  valores,
  opcoes,
  aoMudar,
}: {
  valores: string[]
  opcoes: OpcaoDoFiltro[]
  aoMudar: (valores: string[]) => void
}) {
  const rotulo = ROTULOS_DOS_FILTROS_DAS_TAREFAS.responsavel
  const nomes = valores
    .map((valor) => opcoes.find((cada) => cada.id === valor)?.rotulo)
    .filter((nome): nome is string => nome !== undefined)
  const resumo = valores.length === 0 ? QUALQUER : nomes.join(', ') || String(valores.length)
  return (
    <details className="tela-tarefas__filtro tela-tarefas__multi" data-ativo={valores.length > 0}>
      <summary>
        {rotulo}: {resumo}
      </summary>
      <fieldset className="tela-tarefas__multi-opcoes">
        <legend className="tela-tarefas__so-para-leitor">{rotulo}</legend>
        {opcoes.map((cada) => (
          <label key={cada.id}>
            <input
              type="checkbox"
              checked={valores.includes(cada.id)}
              onChange={(evento) => {
                aoMudar(
                  evento.target.checked
                    ? [...valores, cada.id]
                    : valores.filter((valor) => valor !== cada.id),
                )
              }}
            />
            {cada.rotulo}
          </label>
        ))}
      </fieldset>
    </details>
  )
}

export function Tarefas() {
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()

  const [filtro, setFiltro] = useState<FiltroDasTarefas>(FILTROS_DAS_TAREFAS[0])
  useDefinirSubtitulo(filtro.rotulo.toLocaleLowerCase('pt-BR'))

  const [filtros, setFiltros] = useState<FiltrosDaTela>(lerFiltrosDaSessao)
  useEffect(() => {
    gravarFiltrosNaSessao(filtros)
  }, [filtros])
  const algumFiltro = temFiltro(filtros)
  /* A barra de filtros nasce fechada (protótipo v2) — e aberta quando algum filtro sobreviveu à
     sessão: um recorte escondido seria uma lista misteriosamente curta. */
  const [filtrosAbertos, setFiltrosAbertos] = useState(() => temFiltro(lerFiltrosDaSessao()))

  const consulta = useTarefas({
    quando: filtro.quando,
    concluida: false,
    criadorId: ouNada(filtros.criadorId),
    responsavelIds: filtros.responsavelIds,
    tipoDeTarefaId: ouNada(filtros.tipoDeTarefaId),
    negocioId: ouNada(filtros.negocioId),
    empresaId: ouNada(filtros.empresaId),
    pessoaId: ouNada(filtros.pessoaId),
  })
  //: O universo das opções de Negócio/Empresa/Pessoa: a aba sem chips. Sem chip ligado, é a mesma
  //: chave da consulta acima — nenhuma ida à rede a mais.
  const universo = useTarefas({ quando: filtro.quando, concluida: false })
  const equipe = useResponsaveis()
  const catalogo = useTiposDeTarefa()

  const itens = useMemo(() => consulta.data ?? [], [consulta.data])
  const doUniverso = useMemo(() => universo.data ?? [], [universo.data])

  const idsDeEmpresas = useMemo(
    () => [
      ...new Set(
        [...itens, ...doUniverso]
          .map((item) => item.empresa_do_vinculo_id)
          .filter((id): id is string => id !== null),
      ),
    ],
    [itens, doUniverso],
  )
  const { nomes, carregando: nomesACaminho, falhou: nomesFalharam } =
    useNomesDeEmpresas(idsDeEmpresas)
  const idsDePessoas = useMemo(
    () => [
      ...new Set(
        doUniverso.map((item) => item.pessoa_id).filter((id): id is string => id !== null),
      ),
    ],
    [doUniverso],
  )
  const { nomes: nomesDePessoas } = useNomesDePessoas(idsDePessoas)

  const daEquipe = useMemo(() => equipe.data ?? [], [equipe.data])
  const icones = useMemo(
    () => new Map((catalogo.data ?? []).map((tipo) => [tipo.id, tipo.icone ?? null])),
    [catalogo.data],
  )

  const linhas: LinhaDaTarefa[] = useMemo(
    () =>
      itens.map((tarefa) => ({
        tarefa,
        ...tituloDaTarefa(tarefa.empresa_do_vinculo_id, {
          nomes,
          carregando: nomesACaminho,
          falhou: nomesFalharam,
        }),
        prazo: prazoNaLista(tarefa.prazo),
        responsavel: rotuloDoResponsavel(tarefa.responsavel_id, { sessao, daEquipe }),
        criador: rotuloDoResponsavel(tarefa.criador_id, { sessao, daEquipe }),
        icone: icones.get(tarefa.tipo_de_tarefa_id) ?? null,
      })),
    [itens, nomes, nomesACaminho, nomesFalharam, sessao, daEquipe, icones],
  )

  /* As opções dos chips. "Eu" é fixo e vem primeiro; a equipe inteira (com desativados, FR-65)
     depois, sem repetir quem entrou. */
  const opcoesDePessoasDaEquipe: OpcaoDoFiltro[] = useMemo(() => {
    const eu = sessao?.usuario_id
    return [
      ...(eu === undefined ? [] : [{ id: eu, rotulo: EU }]),
      ...daEquipe
        .filter((cada) => cada.id !== eu)
        .map((cada) => ({ id: cada.id, rotulo: cada.nome })),
    ]
  }, [sessao, daEquipe])
  const opcoesDeTipo: OpcaoDoFiltro[] = useMemo(
    () =>
      (catalogo.data ?? []).map((tipo) => ({
        id: tipo.id,
        rotulo: nomeComIcone(tipo),
      })),
    [catalogo.data],
  )
  const opcoesDeEmpresa: OpcaoDoFiltro[] = useMemo(() => {
    const ids = [
      ...new Set(
        doUniverso
          .map((item) => item.empresa_do_vinculo_id)
          .filter((id): id is string => id !== null),
      ),
    ]
    return ids
      .map((id) => ({ id, rotulo: nomes.get(id) ?? '…' }))
      .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'))
  }, [doUniverso, nomes])
  const opcoesDeNegocio: OpcaoDoFiltro[] = useMemo(() => {
    const vistos = new Map<string, string | null>()
    for (const item of doUniverso) {
      if (item.negocio_id !== null && !vistos.has(item.negocio_id)) {
        vistos.set(item.negocio_id, item.empresa_do_vinculo_id)
      }
    }
    const contagem = new Map<string, number>()
    return [...vistos].map(([id, empresaId]) => {
      const empresa = empresaId === null ? '…' : (nomes.get(empresaId) ?? '…')
      const vez = (contagem.get(empresa) ?? 0) + 1
      contagem.set(empresa, vez)
      return { id, rotulo: vez === 1 ? `Negócio · ${empresa}` : `Negócio · ${empresa} (${vez})` }
    })
  }, [doUniverso, nomes])
  const opcoesDePessoa: OpcaoDoFiltro[] = useMemo(
    () =>
      idsDePessoas
        .map((id) => ({ id, rotulo: nomesDePessoas.get(id) ?? '…' }))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [idsDePessoas, nomesDePessoas],
  )

  function mudarFiltro<Chave extends keyof FiltrosDaTela>(chave: Chave, valor: FiltrosDaTela[Chave]) {
    setFiltros((atuais) => ({ ...atuais, [chave]: valor }))
  }

  const [concluindo, setConcluindo] = useState<Tarefa | null>(null)
  const [sugestaoAceita, setSugestaoAceita] = useState<{
    tarefa: Tarefa
    sugestao: SugestaoDeProximoPasso
  } | null>(null)
  //: O diálogo de criar/editar: `null` fechado, `'nova'` criação avulsa, a Tarefa na edição.
  const [emEdicao, setEmEdicao] = useState<Tarefa | 'nova' | null>(null)
  const [excluindo, setExcluindo] = useState<Tarefa | null>(null)
  const exclusao = useExcluirTarefa()

  /** O botão que abriu o diálogo da vez — para devolver o foco quando ele fechar. */
  const gatilho = useRef<HTMLElement | null>(null)
  /** A aba ativa: o alvo estável do foco quando o gatilho saiu do DOM (a linha sumiu). */
  const abaAtiva = useRef<HTMLButtonElement>(null)

  function focarAAbaAtiva() {
    abaAtiva.current?.focus()
  }

  /** Devolve o foco ao gatilho se ele ainda estiver montado; senão, à aba ativa. */
  function devolverOFoco() {
    const alvo = gatilho.current
    gatilho.current = null
    if (alvo !== null && alvo.isConnected) {
      alvo.focus()
      return
    }
    focarAAbaAtiva()
  }

  const concluiuDeVerdade = useRef(false)

  /* As ações da linha, estáveis entre renders: as colunas da tabela dependem delas. */
  const acoes: AcoesDaLinha = useMemo(
    () => ({
      aoConcluir: (tarefa, evento) => {
        gatilho.current = evento.currentTarget
        setSugestaoAceita(null)
        setConcluindo(tarefa)
      },
      aoEditar: (tarefa, evento) => {
        gatilho.current = evento.currentTarget
        setEmEdicao(tarefa)
      },
      aoExcluir: (tarefa, evento) => {
        gatilho.current = evento.currentTarget
        exclusao.reset()
        setExcluindo(tarefa)
      },
    }),
    // `exclusao.reset` é estável no React Query v5.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  function fecharAConclusao() {
    setConcluindo(null)
    if (concluiuDeVerdade.current) {
      concluiuDeVerdade.current = false
      gatilho.current = null
      focarAAbaAtiva()
      return
    }
    devolverOFoco()
  }

  const carregando = !consulta.isError && (consulta.isLoading || consulta.data === undefined)
  const vazio = !carregando && !consulta.isError && linhas.length === 0

  const escopo =
    sessao !== undefined && ehDeGestao(sessao.escopo_de_carteira)
      ? ESCOPO_DAS_TAREFAS_DA_GESTAO
      : ESCOPO_DAS_TAREFAS

  const limparFiltros = (
    <button
      type="button"
      className="tela-tarefas__limpar"
      onClick={() => {
        setFiltros(SEM_FILTROS)
      }}
    >
      {LIMPAR_FILTROS}
    </button>
  )

  return (
    <div className="tela-tarefas">
      <div className="tela-tarefas__topo">
        <div className="tela-tarefas__abas" role="group" aria-label={ROTULO_DOS_FILTROS}>
          {FILTROS_DAS_TAREFAS.map((cada) => (
            <button
              key={cada.rotulo}
              type="button"
              ref={cada.rotulo === filtro.rotulo ? abaAtiva : undefined}
              className="tela-tarefas__aba"
              aria-pressed={cada.rotulo === filtro.rotulo}
              onClick={() => {
                setFiltro(cada)
              }}
            >
              {cada.rotulo}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="tela-tarefas__botao tela-tarefas__botao--pequeno"
          aria-expanded={filtrosAbertos}
          onClick={() => {
            setFiltrosAbertos((aberto) => !aberto)
          }}
        >
          {filtrosAbertos ? ROTULO_DE_OCULTAR_OS_FILTROS : ROTULO_DOS_FILTROS_DA_TELA}
          {algumFiltro ? ' · ativos' : ''}
        </button>
        <span className="tela-tarefas__escopo">
          {consulta.data === undefined ? escopo : `${contagemDeTarefas(linhas.length)} · ${escopo}`}
        </span>
      </div>
      {/* `+ Tarefa` mora no cabeçalho do shell (protótipo v2); o nome acessível é o de sempre. */}
      <AcoesDoCabecalho>
        <button
          type="button"
          className="tela-tarefas__nova"
          aria-label={NOVA_TAREFA}
          onClick={(evento) => {
            gatilho.current = evento.currentTarget
            setEmEdicao('nova')
          }}
        >
          {MAIS_TAREFA}
        </button>
      </AcoesDoCabecalho>
      {filtrosAbertos ? (
      <div className="tela-tarefas__filtros" role="group" aria-label={ROTULO_DA_BARRA_DE_FILTROS}>
        <ChipDeEscolha
          id={IDS_DOS_FILTROS.criador}
          rotulo={ROTULOS_DOS_FILTROS_DAS_TAREFAS.criador}
          valor={filtros.criadorId}
          opcoes={opcoesDePessoasDaEquipe}
          aoMudar={(valor) => {
            mudarFiltro('criadorId', valor)
          }}
        />
        <ChipDosResponsaveis
          valores={filtros.responsavelIds}
          opcoes={opcoesDePessoasDaEquipe}
          aoMudar={(valores) => {
            mudarFiltro('responsavelIds', valores)
          }}
        />
        <ChipDeEscolha
          id={IDS_DOS_FILTROS.tipo}
          rotulo={ROTULOS_DOS_FILTROS_DAS_TAREFAS.tipo}
          valor={filtros.tipoDeTarefaId}
          opcoes={opcoesDeTipo}
          aoMudar={(valor) => {
            mudarFiltro('tipoDeTarefaId', valor)
          }}
        />
        <ChipDeEscolha
          id={IDS_DOS_FILTROS.negocio}
          rotulo={ROTULOS_DOS_FILTROS_DAS_TAREFAS.negocio}
          valor={filtros.negocioId}
          opcoes={opcoesDeNegocio}
          aoMudar={(valor) => {
            mudarFiltro('negocioId', valor)
          }}
        />
        <ChipDeEscolha
          id={IDS_DOS_FILTROS.empresa}
          rotulo={ROTULOS_DOS_FILTROS_DAS_TAREFAS.empresa}
          valor={filtros.empresaId}
          opcoes={opcoesDeEmpresa}
          aoMudar={(valor) => {
            mudarFiltro('empresaId', valor)
          }}
        />
        <ChipDeEscolha
          id={IDS_DOS_FILTROS.pessoa}
          rotulo={ROTULOS_DOS_FILTROS_DAS_TAREFAS.pessoa}
          valor={filtros.pessoaId}
          opcoes={opcoesDePessoa}
          aoMudar={(valor) => {
            mudarFiltro('pessoaId', valor)
          }}
        />
        {algumFiltro ? limparFiltros : null}
      </div>
      ) : null}
      {consulta.isError ? (
        <div className="tela-tarefas__recusa">
          <p className="tela-tarefas__erro" role="alert">
            {mensagemDe(consulta.error)}
          </p>
          <button
            type="button"
            className="tela-tarefas__botao"
            onClick={() => {
              void consulta.refetch()
            }}
          >
            {TENTAR_DE_NOVO}
          </button>
        </div>
      ) : carregando ? (
        <p className="tela-tarefas__aviso" role="status">
          {CARREGANDO}
        </p>
      ) : vazio ? (
        algumFiltro ? (
          <div className="tela-tarefas__recusa">
            <p className="tela-tarefas__aviso">{SEM_TAREFAS_COM_FILTROS}</p>
            {limparFiltros}
          </div>
        ) : (
          <p className="tela-tarefas__aviso">{SEM_TAREFAS_NO_RECORTE[filtro.rotulo]}</p>
        )
      ) : (
        <>
          {nomesFalharam ? (
            <p className="tela-tarefas__aviso tela-tarefas__aviso--recusa" role="alert">
              {NOMES_NAO_VIERAM}
            </p>
          ) : null}
          {equipe.isError ? (
            <p className="tela-tarefas__aviso tela-tarefas__aviso--recusa" role="alert">
              {EQUIPE_NAO_VEIO}
            </p>
          ) : null}
          {ehCelular ? (
            <ul className="tela-tarefas__cartoes" aria-label={ROTULO_DA_TELA_DE_TAREFAS}>
              {linhas.map((linha) => (
                <CartaoDaTarefa key={linha.tarefa.id} linha={linha} acoes={acoes} />
              ))}
            </ul>
          ) : (
            <TabelaDasTarefas linhas={linhas} acoes={acoes} />
          )}
        </>
      )}

      <ConclusaoDeTarefa
        concluindo={concluindo}
        aoConcluir={() => {
          concluiuDeVerdade.current = true
        }}
        aoFechar={fecharAConclusao}
        aoAceitarASugestao={(tarefa, sugestao) => {
          setSugestaoAceita({ tarefa, sugestao })
        }}
        aoRecusarASugestao={focarAAbaAtiva}
      />

      {sugestaoAceita === null ? null : (
        <NovaTarefaDaSugestao
          origem={sugestaoAceita.tarefa}
          sugestao={sugestaoAceita.sugestao}
          aoFechar={() => {
            setSugestaoAceita(null)
            focarAAbaAtiva()
          }}
        />
      )}

      {emEdicao === null ? null : (
        <DialogoDaTarefa
          tarefa={emEdicao === 'nova' ? undefined : emEdicao}
          aoFechar={() => {
            setEmEdicao(null)
            devolverOFoco()
          }}
        />
      )}

      {excluindo === null ? null : (
        <Dialogo
          titulo={TITULO_DA_EXCLUSAO}
          subtitulo={SUBTITULO_DA_EXCLUSAO}
          rotuloDoConfirmar={EXCLUIR}
          ocupado={exclusao.isPending}
          erro={exclusao.isError ? exclusao.error : undefined}
          aoFechar={() => {
            if (exclusao.isPending) {
              return
            }
            setExcluindo(null)
            devolverOFoco()
          }}
          aoConfirmar={() => {
            exclusao.mutate(excluindo.id, {
              onSuccess: () => {
                setExcluindo(null)
                anunciar(TAREFA_EXCLUIDA)
                gatilho.current = null
                focarAAbaAtiva()
              },
            })
          }}
        >
          <p className="tela-tarefas__dica">{excluindo.titulo}</p>
        </Dialogo>
      )}
    </div>
  )
}
